import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

// Filter color galleries from real media filenames/alt text. Previous implementation
// checked alt alone; uploaded product photos commonly have an empty alt field.
const STRICT_COLOR_GALLERY_PRODUCTS = new Set([
  'round-toe-faux-fur-lined-elastic-gusset-snow-ankle-boots-kaia',
  'round-toe-faux-fur-trimmed-slip-on-snow-boots-nyla',
  'round-toe-faux-leather-faux-fur-lined-platform-snow-boots-freya',
  'round-toe-faux-fur-trimmed-platform-snow-boots-lumi',
  'round-toe-faux-fur-lined-lace-up-wedge-snow-ankle-boots-aspen',
  'square-toe-pu-side-zip-block-heel-ankle-boots-sloane',
  'pointed-toe-embroidered-suede-look-western-ankle-boots-dakota',
  'round-toe-leopard-bow-suede-platform-loafers',
]);
const ELVESSIA_MOBILE_PRIME_LIMIT = 8;
const MEDIA_COLOR_OVERRIDES = {
  'elowen-woven-square-toe-flats': [
    { includes: 'square toe and woven upper detail', color: 'silver' },
    { includes: 'rubber outsole detail', color: 'silver' },
  ],
  'square-toe-pu-side-zip-block-heel-ankle-boots-sloane': [
    { includes: 'square-toe patent pu ankle boots in black', color: 'black patent' },
    { includes: 'black patent ankle boots', color: 'black patent' },
  ],
};

/** @extends Component */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    this.#filterInitialGallery();
    this.#primeMobileMedia(this);
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #normalize(value) { return String(value ?? '').trim().toLowerCase(); }
  #getVariantPicker(source) {
    if (source instanceof Element) {
      const picker = source.matches('variant-picker') ? source : source.closest('variant-picker');
      if (picker) return picker;
    }
    return this.closest('.shopify-section')?.querySelector('variant-picker') ?? null;
  }

  #getColorContext(source) {
    const picker = this.#getVariantPicker(source);
    if (!(picker instanceof HTMLElement)) return null;
    const colorControls = Array.from(picker.querySelectorAll('input[data-option-name], select option[data-option-name]'))
      .filter(control => this.#normalize(control.dataset.optionName) === 'color');
    const knownColors = [...new Set(colorControls.map(control => this.#normalize(control.value)).filter(Boolean))];
    const productHandle = picker.dataset.productHandle ?? '';
    if (knownColors.length < 2 && !STRICT_COLOR_GALLERY_PRODUCTS.has(productHandle)) return null;
    const checkedColor = Array.from(picker.querySelectorAll('input:checked[data-option-name]'))
      .find(input => this.#normalize(input.dataset.optionName) === 'color');
    let selectedColor = checkedColor ? this.#normalize(checkedColor.value) : '';
    if (!selectedColor) {
      for (const select of picker.querySelectorAll('select')) {
        if (!(select instanceof HTMLSelectElement)) continue;
        const selected = select.selectedOptions[0];
        if (this.#normalize(selected?.dataset.optionName) === 'color') {
          selectedColor = this.#normalize(selected.value);
          break;
        }
      }
    }
    if (!selectedColor || !knownColors.includes(selectedColor)) return null;
    return { selectedColor, knownColors, productHandle };
  }

  #filterInitialGallery() {
    const context = this.#getColorContext();
    if (context) this.#filterGalleryByColor(this, context);
  }

  #primeMobileMedia(gallery) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const slides = Array.from(gallery.querySelectorAll('slideshow-slides > slideshow-slide'));
    slides.forEach((slide, index) => {
      const image = slide.querySelector('img.product-media__image');
      if (!(image instanceof HTMLImageElement)) return;
      if (index < ELVESSIA_MOBILE_PRIME_LIMIT) {
        image.loading = 'eager';
        image.fetchPriority = index === 0 ? 'high' : 'low';
      } else {
        image.loading = 'lazy';
        image.fetchPriority = 'low';
      }
    });
  }

  #mediaColor(slide, context) {
    const img = slide.querySelector('img');
    const alt = this.#normalize(img?.getAttribute('alt'));
    const rules = MEDIA_COLOR_OVERRIDES[context.productHandle] ?? [];
    const override = rules.find(rule => alt.includes(rule.includes));
    if (override) return override.color;
    const url = img?.getAttribute('src') || img?.getAttribute('data-src') || img?.getAttribute('srcset') || '';
    // Color may appear in either the original asset filename or the image alt text.
    // Convert hyphens, underscores and URL encoding to spaces, then compare whole words.
    let source = url;
    try { source = decodeURIComponent(url); } catch (_) { /* retain raw source */ }
    const normalized = `${alt} ${source}`.toLowerCase().replace(/[-_+%]/g, ' ');
    const matches = context.knownColors.filter(color => {
      const escaped = color.replace(/[-_+%]/g, ' ').replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
      return new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, 'i').test(normalized);
    });
    if (!matches.length) return null;
    // Prefer more specific color names such as Black Patent over Black.
    return matches.sort((a, b) => b.length - a.length)[0];
  }

  #filterGalleryByColor(gallery, context) {
    const slides = Array.from(gallery.querySelectorAll('slideshow-slides > slideshow-slide'));
    if (!slides.length) return;
    const identified = slides.map(slide => this.#mediaColor(slide, context));
    // Never discard a gallery unless at least two distinguishable color groups exist.
    const distinct = new Set(identified.filter(Boolean));
    if (distinct.size < 2) return;
    let currentGroup = null;
    const keep = new Set();
    identified.forEach((color, index) => {
      if (color) currentGroup = color;
      if (currentGroup === context.selectedColor) keep.add(index);
    });
    if (!keep.size || keep.size === slides.length) return;
    this.#removeByIndex(slides, keep);
    this.#removeByIndex(Array.from(gallery.querySelectorAll('.media-gallery__grid > li')), keep);
    this.#removeByIndex(Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery > li')), keep);
    this.#removeByIndex(Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')), keep);
    for (const dots of gallery.querySelectorAll('.slideshow-controls__dots')) this.#removeByIndex(Array.from(dots.children), keep);
    for (const thumbnails of gallery.querySelectorAll('.slideshow-controls__thumbnails')) this.#removeByIndex(Array.from(thumbnails.children), keep);
    this.#reindexGallery(gallery);
  }

  #removeByIndex(nodes, keep) {
    nodes.forEach((node, index) => { if (!keep.has(index)) node.remove(); });
  }

  #rewriteAction(root, attribute, pattern, replacement) {
    for (const element of [root, ...root.querySelectorAll('*')]) {
      const value = element.getAttribute(attribute);
      if (value && pattern.test(value)) element.setAttribute(attribute, value.replace(pattern, replacement));
    }
  }

  #reindexGallery(gallery) {
    const slides = Array.from(gallery.querySelectorAll('slideshow-slides > slideshow-slide'));
    const slideCount = slides.length;
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      if (slide instanceof HTMLElement) slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
      this.#rewriteAction(slide, 'on:click', /\/open\/\d+/, `/open/${index}`);
    });
    for (const component of gallery.querySelectorAll('slideshow-component')) {
      if (component instanceof HTMLElement) {
        component.setAttribute('initial-slide', '0');
        component.style.setProperty('--slideshow-timeline', Array.from({ length: slideCount }, (_, index) => `--slide-${index}`).join(','));
      }
    }
    const gridItems = Array.from(gallery.querySelectorAll('.media-gallery__grid > li'));
    gridItems.forEach((item, index) => {
      this.#rewriteAction(item, 'on:click', /\/zoom\/\d+/, `/zoom/${index}`);
      this.#rewriteAction(item, 'on:pointerenter', /\/preloadImage\/\d+/, `/preloadImage/${index}`);
    });
    for (const dots of gallery.querySelectorAll('.slideshow-controls__dots')) {
      Array.from(dots.querySelectorAll('button')).forEach((button, index) => {
        button.setAttribute('on:click', `/select/${index}`);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        if (button instanceof HTMLElement) button.style.setProperty('animation-timeline', `--slide-${index}`);
        button.textContent = String(index + 1);
      });
    }
    for (const thumbnails of gallery.querySelectorAll('.slideshow-controls__thumbnails')) {
      Array.from(thumbnails.querySelectorAll('.slideshow-controls__thumbnail')).forEach((button, index) => {
        button.setAttribute('on:click', `/select/${index}`);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      });
    }
    Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')).forEach((button, index) => {
      button.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      button.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    });
    for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
      const current = counter.querySelector('[ref="current"]');
      if (current) current.textContent = '1';
      const totalNode = Array.from(counter.childNodes).filter(node => node.nodeType === Node.TEXT_NODE).at(-1);
      if (totalNode) totalNode.textContent = String(slideCount);
    }
  }

  #handleProductSelect = event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise.then(({ detail }) => {
      if (!detail?.html) return;
      const newMediaGallery = detail.html.querySelector('media-gallery');
      if (!newMediaGallery) return;

      // Read the selected color from Shopify's NEW variant HTML, not the old DOM.
      // The old DOM can still report the previous color when productSelect first fires.
      const newVariantPicker = detail.html.querySelector('variant-picker');
      const colorContext = newVariantPicker
        ? this.#getColorContext(newVariantPicker)
        : this.#getColorContext(event.target);

      if (colorContext) this.#filterGalleryByColor(newMediaGallery, colorContext);
      this.#primeMobileMedia(newMediaGallery);
      this.replaceWith(newMediaGallery);
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
    });
  };
  #handleZoomMediaSelected = async event => { this.slideshow?.select(event.detail.index, undefined, { animate: false }); };
  zoom(index, event) { this.refs.zoomDialogComponent?.open(index, event); }
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (zoomDialogMedia) this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }
  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
